import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      console.log('🔍 Verifying email with token:', token?.substring(0, 10) + '...');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please check your email.');
        return;
      }

      try {
        console.log('📧 Sending verification request...');
        const response = await axios.post(`${API}/api/auth/verify-email?token=${token}`);
        
        console.log('✅ Verification successful:', response.data);
        
        // Check if already verified
        if (response.data.already_verified) {
          setStatus('success');
          setMessage('Your email is already verified! Redirecting you now...');
        } else {
          setStatus('success');
          setMessage(response.data.message || 'Email verified successfully!');
        }
        
        // Refresh user data to update banner
        console.log('🔄 Refreshing user data...');
        await refreshUser();
        
        // Redirect to home after 2 seconds
        setTimeout(() => {
          console.log('➡️ Redirecting to home...');
          navigate('/');
        }, 2000);
        
      } catch (error) {
        console.error('❌ Verification failed:', error);
        setStatus('error');
        const errorMsg = error.response?.data?.detail || 'Verification failed. The link may have expired.';
        setMessage(errorMsg);
        
        // Even on error, redirect after 4 seconds to login page
        setTimeout(() => {
          navigate('/');
        }, 4000);
      }
    };

    verifyEmail();
  }, [searchParams, navigate, refreshUser]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {status === 'verifying' && (
          <>
            <Loader2 
              size={64} 
              style={{ 
                color: '#6366f1', 
                margin: '0 auto 20px',
                animation: 'spin 1s linear infinite'
              }} 
            />
            <h1 style={{ fontSize: '24px', marginBottom: '10px', color: '#1f2937' }}>
              Verifying Your Email
            </h1>
            <p style={{ color: '#6b7280', fontSize: '16px' }}>
              Please wait while we verify your email address...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 
              size={64} 
              style={{ 
                color: '#10b981', 
                margin: '0 auto 20px'
              }} 
            />
            <h1 style={{ fontSize: '24px', marginBottom: '10px', color: '#1f2937' }}>
              Email Verified! 🎉
            </h1>
            <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '20px' }}>
              {message}
            </p>
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>
              Redirecting to app in 2 seconds...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle 
              size={64} 
              style={{ 
                color: '#ef4444', 
                margin: '0 auto 20px'
              }} 
            />
            <h1 style={{ fontSize: '24px', marginBottom: '10px', color: '#1f2937' }}>
              Verification Link Issue
            </h1>
            <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '20px' }}>
              {message}
            </p>
            <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '20px' }}>
              Don't worry! If your email is already verified, you can log in normally.
              Redirecting in 4 seconds...
            </p>
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Go to Home Now
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default VerifyEmail;
